import { clsx } from "clsx";
import React, { useMemo } from "react";

import { Price } from "@bared/ui";

import CountdownTimer from "@components/CountdownTimer/CountdownTimer";
import { Reviews } from "@components/Reviews/Reviews";
import ReviewsInline from "@components/Reviews/ReviewsInline";
import { Sections } from "@components/Sections/Sections";
import tw, { styled } from "twin.macro";
import RichText from "../RichText";
import FlashSale from "./FlashSale/FlashSale";
import { Images } from "./Images/Images";
import { ServiceHighlights } from "./ServiceHighlights/ServiceHighlights";
import { Title, TitleWrapper } from "./Shared";
import { withProduct } from "./withProduct";

import { useSite } from "~/hooks/useSite";
import { useProduct } from "~/templates/product/useProduct";

import {
  Link,
  Container,
  Breadcrumbs,
  ProductLayout,
  Badge,
  BuyNowPayLaterWidget,
} from "@bared/ui";

const SubTitle = tw.p`
  text-body leading-body text-caption
`;
const InlineReviews = styled(ReviewsInline)`
  ${tw`whitespace-nowrap`}
  & .ruk-rating-snippet-count {
    text-decoration: underline;
    text-underline-offset: 0.1rem;
  }
`;
const Description = styled(RichText)`
  ${tw`block mb-mini`}
  p {
    ${tw`text-left`}
  }
`;
const Message = styled(Description)`
  ${tw`italic md:-mt-midi`}
  p {
    ${tw`pb-0 text-[13px]`}
  }
`;

const SectionsWrapper = tw.div``;

const OutOfStockMessage = styled.div`
  ${tw`bg-white border-l-4 px-midi py-mini border-warning`}

  p {
    ${tw`text-warning leading-body text-body`}
  }

  a {
    ${tw`font-medium underline`}
  }
`;

export const Product = withProduct(
  ({
    badge,
    content,
    contentByTag,
    flashSaleDescription,
    flashSaleTitle,
    handle,
    heroImage,
    images,
    isFlashSaleProduct,
    onSale,
    parentCollection,
    paths,
    preorderMessage,
    productGroup,
    product,
    saleTag,
    sectionsRef,
    selectedVariant,
    seoFeatureDescription,
    showTimers,
    sku,
    subTitle,
    video,
    outOfStockMessage,
    howToStyleSection,
    asSeenOnSection,
    productName,
    productGender,
    productStatus,
    productSpecificContent,
    productRecommendations,
    imageGallery,
    swatches,
    siblingColours,
    reviewsSummary,
    reviewNugget,
    featureAndShoeCareSheet,
    children,
  }) => {
    const { isFinalSale, price, compareAtPrice } = useProduct();

    const site = useSite();

    const notify = useMemo(
      () => ["Core", "Seasonal"].includes(productStatus),
      [productStatus],
    );

    return (
      <>
        <div className="bg-neutral">
          <Container
            className={clsx(!imageGallery && "pb-maxi")}
            maxWidth={!!imageGallery ? "default" : "xl"}
          >
            {!imageGallery && (
              <Breadcrumbs className="mb-maxi hidden md:flex">
                {paths
                  .map((path) => {
                    if (!path.url) return;
                    return (
                      <Link href={path.url} variant="link">
                        {path.title}
                      </Link>
                    );
                  })
                  .filter(Boolean)}
              </Breadcrumbs>
            )}
            <div className="space-y-mini">
              <ProductLayout
                legacy={!imageGallery}
                stickyOffsetClassName={
                  imageGallery ? "top-[122px]" : "top-[152px]"
                }
                imageGallery={
                  imageGallery ?? (
                    <Images
                      image={heroImage}
                      images={images}
                      video={video}
                      badge={badge}
                    />
                  )
                }
              >
                <div
                  className={clsx(
                    "flex flex-col space-y-mini",
                    !!imageGallery && "py-mini",
                  )}
                >
                  {!!imageGallery && (
                    <Breadcrumbs>
                      {paths
                        .map((path) => {
                          if (!path.url) return;
                          return (
                            <Link href={path.url} variant="link">
                              {path.title}
                            </Link>
                          );
                        })
                        .filter(Boolean)}
                    </Breadcrumbs>
                  )}
                  <div className="space-y-micro">
                    <TitleWrapper className="flex items-start justify-between">
                      <div className="flex flex-col">
                        <Title className="mb-micro">{productName}</Title>
                        <Price price={price} compareAtPrice={compareAtPrice} />
                      </div>
                      <div className="flex flex-col items-end gap-micro">
                        <InlineReviews productGroup={productGroup} />
                        {!!isFinalSale && (
                          <Badge variant="secondary" size="small">
                            Final Sale
                          </Badge>
                        )}
                      </div>
                    </TitleWrapper>
                    {"AU" === site.countryCode && (
                      <BuyNowPayLaterWidget price={price} />
                    )}
                    {!!subTitle ? (
                      <SubTitle>
                        <span className="font-medium">Colour: </span>
                        {subTitle}
                      </SubTitle>
                    ) : null}
                    {swatches && <>{swatches}</>}
                    {isFlashSaleProduct && (
                      <FlashSale
                        title={flashSaleTitle}
                        description={flashSaleDescription}
                      />
                    )}
                  </div>
                  <div>
                    {notify || product?.availableForSale ? (
                      <>
                        {selectedVariant?.isPreOrder &&
                          preorderMessage?.length > 0 && (
                            <Message html={`<p>${preorderMessage}</p>`} />
                          )}
                      </>
                    ) : (
                      <OutOfStockMessage>{outOfStockMessage}</OutOfStockMessage>
                    )}
                    <div className="mb-mini">{children}</div>
                    {showTimers && (
                      <CountdownTimer
                        layout={"product"}
                        collection={parentCollection}
                      />
                    )}

                    {seoFeatureDescription && (
                      <div className="mb-mini">{seoFeatureDescription}</div>
                    )}
                    {featureAndShoeCareSheet && <>{featureAndShoeCareSheet}</>}
                    {reviewNugget && (
                      <div className="mt-midi">{reviewNugget}</div>
                    )}
                    <ServiceHighlights />
                  </div>
                </div>
              </ProductLayout>
              {howToStyleSection && <>{howToStyleSection}</>}
              {productSpecificContent && (
                <div className="-mx-mini md:-mx-maxi">
                  <Sections page={productSpecificContent} />
                </div>
              )}
              {asSeenOnSection && <>{asSeenOnSection}</>}
            </div>

            <Reviews
              productGroup={productGroup}
              productHandle={handle}
              sku={sku}
              gender={productGender}
              siblingColours={siblingColours}
              reviewsSummary={reviewsSummary}
            />
          </Container>
        </div>

        <Container>
          {productRecommendations && <>{productRecommendations}</>}
        </Container>

        {contentByTag.content && <Sections page={contentByTag} />}

        <SectionsWrapper ref={sectionsRef}>
          {content.content && <Sections page={content} />}
        </SectionsWrapper>
      </>
    );
  },
);

export default Product;
